import { FeedbackInfo } from '@components/Feedback';
import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

import { FILTER_OPTIONS_PAGE_SIZE } from './constants';
import { useFilter } from './filter';
import { OptionData } from './interfaces';
import fetchOptions from './services/fetchOptions';

type UsePaginatedFilterOptionsParams = {
  column: string;
  search: string;
  enabled: boolean;
};

export function usePaginatedFilterOptions({
  column,
  search,
  enabled,
}: UsePaginatedFilterOptionsParams) {
  const { filters, getQueryString } = useFilter();

  const url = filters.find((filter) => filter.column === column)?.url;
  // Excludes this column's own selection, so picking an option here does not
  // change the key (no refetch); a change in any other filter or in the order
  // does, which restarts the list from page 1.
  const queryString = getQueryString(column);

  const query = useInfiniteQuery<OptionData[]>(
    ['filter-options', url, queryString, search],
    ({ pageParam = 1 }) => {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      return fetchOptions(
        url as string,
        `${queryString}&page=${pageParam}&limit=${FILTER_OPTIONS_PAGE_SIZE}${searchParam}`,
      );
    },
    {
      enabled: enabled && !!url,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
      retry: 1,
      // The endpoint returns a plain array, so a full page means there may be more.
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === FILTER_OPTIONS_PAGE_SIZE
          ? allPages.length + 1
          : undefined,
      onError: (error: unknown) => {
        FeedbackInfo({
          mainText: 'Atenção!',
          subText: error instanceof Error ? error.message : '',
        });
      },
    },
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = query;

  const options = useMemo(() => {
    const seen = new Set<string>();
    const pages: OptionData[][] = data?.pages ?? [];
    return ([] as OptionData[]).concat(...pages).filter((option) => {
      if (seen.has(option.id)) return false;
      seen.add(option.id);
      return true;
    });
  }, [data]);

  return {
    options,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    isFetching,
  };
}

export default usePaginatedFilterOptions;
